import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";
import { useRosterAndStaffQueryDataUpdater } from "./useRosterAndStaffQueryDataUpdater";

type Props = {
  entityId: string;
  seasonId: string;
  fixtureId: string;
};

export const useFetchFixtureStaff = ({ entityId, seasonId, fixtureId }: Props) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["fixtureStaff", sport, organizationId, entityId, seasonId, fixtureId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff`, {
        defaultErrorMessage: "Can't fetch fixture staff",
      }),
  });
};

export const useFetchPublishedFixtureStaff = ({
  entityId,
  seasonId,
  fixtureId,
  enabled,
}: Props & { enabled: boolean }) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["fixtureStaff", "published", sport, organizationId, entityId, seasonId, fixtureId],
    queryFn: () =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff/published`,
        { defaultErrorMessage: "Can't fetch published fixture staff" }
      ),
    enabled,
  });
};

export const useDeleteFixtureStaffPersons = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, data }: Props & { data: any }) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff`,
        { method: "DELETE", body: data }
      );
    },
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { data: any }) => {
      replaceRecords(["fixtureStaff", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staffPerson.updated" }));
    },
  });
};

export const useAddFixtureStaffPersons = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, data }: Props & { data: any }) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff`,
        { method: "POST", body: data }
      );
    },
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { data: any }) => {
      replaceRecords(["fixtureStaff", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staffPerson.updated" }));
    },
  });
};

export const useUpdateFixtureStaffPersonInternal = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, personId, data }: Props & { personId: string; data: any }) =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff/${personId}`,
        { method: "PUT", body: data }
      ),
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { personId: string; data: any }) => {
      replaceRecords(["fixtureStaff", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staffPerson.updated" }));
    },
  });
};

export const useUpdateFixtureStaffPersonDc = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();
  const { replaceRecords } = useRosterAndStaffQueryDataUpdater();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, dcPersonId, data }: Props & { dcPersonId: string; data: any }) =>
      callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/staff/dc/${dcPersonId}`,
        { method: "PUT", body: data }
      ),
    onSuccess: (response: any, { entityId, seasonId, fixtureId }: Props & { dcPersonId: string; data: any }) => {
      replaceRecords(["fixtureStaff", sport, organizationId, entityId, seasonId, fixtureId], response);
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.staffPerson.updated" }));
    },
  });
};
